/**
 * TeamManagement - Manage team members and invitations
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Users, UserPlus, Mail, MoreVertical, Shield, Clock,
  Loader2, CheckCircle, XCircle, Send, Pencil, Trash2,
  Calendar, RefreshCw, Eye, UserCheck, AlertCircle, Link2, Search
} from 'lucide-react';
import api from '@/lib/api';

// iter-438k · bug#3 — inline autocomplete that searches every user pool
// (HR, delegates, security, kitchen, housing, admins) so team invites can
// re-use people who already exist in the system instead of duplicating.
const PoolSearchAutocomplete = ({ onPick }) => {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const timer = React.useRef(null);

  React.useEffect(() => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/identity/people/search?q=${encodeURIComponent(q)}&limit=12`);
        setResults(r.data.results || []);
        setOpen(true);
      } catch (e) {
        setResults([]);
      } finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search HR, delegates, security, kitchen…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
          data-testid="team-invite-search-existing"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg max-h-64 overflow-y-auto" data-testid="team-invite-search-results">
          {results.map((p) => (
            <button
              type="button"
              key={p.email}
              onClick={() => { onPick(p); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-0"
              data-testid={`team-invite-search-pick-${p.email}`}
            >
              <div className="text-sm font-medium">{p.name || p.email}</div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <span>{p.email}</span>
                <span className="flex gap-1">
                  {(p.sources || []).map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px] py-0 px-1">{s}</Badge>
                  ))}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && q.length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white p-3 text-sm text-slate-500 shadow-lg" data-testid="team-invite-search-empty">
          No matches. Type the email below to invite as a new person.
        </div>
      )}
    </div>
  );
};

const TeamManagement = ({ events = [] }) => {
  const queryClient = useQueryClient();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteData, setInviteData] = useState({ email: '', first_name: '', last_name: '', role_id: 'viewer', event_id: '' });
  const [createData, setCreateData] = useState({ email: '', first_name: '', last_name: '', password: '', global_role: 'viewer', event_id: '' });
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applyTemplateTarget, setApplyTemplateTarget] = useState(null);
  const [inviteFilter, setInviteFilter] = useState('pending');
  // The one moment a raw invite link exists — straight after create / resend.
  const [freshLink, setFreshLink] = useState(null);

  const absoluteLink = (link) =>
    (link || '').startsWith('http') ? link : `${window.location.origin}${link}`;

  // Event-scoped mode: when exactly one event is passed, use per-event team endpoints
  const scopedEventId = events.length === 1 ? events[0]?.id : null;
  const teamEndpoint = scopedEventId ? `/events/${scopedEventId}/team` : '/admin/team';

  // Fetch team members (scoped or global)
  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ['team-members', scopedEventId],
    queryFn: () => api.get(teamEndpoint).then(r => r.data),
  });

  // Fetch invitations
  const { data: invitations = [], isLoading: loadingInvitations } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => api.get('/admin/invitations').then(r => r.data),
  });

  // Fetch roles
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get('/admin/roles').then(r => r.data),
  });

  // Fetch role templates
  const { data: roleTemplates = [] } = useQuery({
    queryKey: ['role-templates'],
    queryFn: () => api.get('/admin/role-templates').then(r => r.data),
  });

  // Create invitation mutation
  const inviteMutation = useMutation({
    mutationFn: (data) => {
      // Auto-scope: if the user is on an event-scoped Team Management page,
      // force event_id to that event so the invitee lands on the correct team.
      const payload = { ...data };
      if (scopedEventId && !payload.event_id) {
        payload.event_id = scopedEventId;
      }
      return api.post('/admin/invitations', payload);
    },
    onSuccess: (res) => {
      // iter-438k · unified verdict — the endpoint now returns one of
      // added_directly / reactivated / invitation_sent. We surface the
      // right toast for each so admins understand what actually happened.
      const verdict = res?.data?.verdict;
      if (verdict === 'added_directly') {
        toast.success(res.data.message || `Added ${res.data.person?.name || res.data.email} to the team`);
      } else if (verdict === 'reactivated') {
        toast.success(res.data.message || 'Existing membership reactivated');
      } else {
        toast.success('Invitation email sent');
        if (res?.data?.invite_link) {
          setFreshLink({ email: res.data.email || '', url: absoluteLink(res.data.invite_link) });
        }
      }
      queryClient.invalidateQueries(['invitations']);
      queryClient.invalidateQueries(['team-members', scopedEventId]);
      setIsInviteOpen(false);
      setInviteData({ email: '', first_name: '', last_name: '', role_id: 'viewer', event_id: '' });
    },
    onError: (err) => {
      // 409 already_member returns a structured detail object.
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof d === 'object' && d?.code === 'already_member') {
        toast.warning(d.message || 'Already a member of this team');
      } else if (typeof d === 'string') {
        toast.error(d);
      } else {
        toast.error(d?.message || 'Failed to add to team');
      }
    },
  });

  // Create member directly mutation
  const createMemberMutation = useMutation({
    mutationFn: (data) => {
      if (scopedEventId) {
        // Event-scoped add — uses the dedicated /events/{id}/team endpoint
        return api.post(`/events/${scopedEventId}/team`, {
          email: data.email,
          name: `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.email.split('@')[0],
          role: data.global_role,
          password: data.password,
        });
      }
      // Platform-wide create (super admin only)
      const payload = { ...data };
      if (data.event_id) {
        payload.assigned_events = [data.event_id];
        payload.event_roles = { [data.event_id]: data.global_role };
      }
      delete payload.event_id;
      return api.post('/admin/team', payload);
    },
    onSuccess: () => {
      toast.success(scopedEventId ? 'Added to event team' : 'Team member created successfully');
      queryClient.invalidateQueries(['team-members', scopedEventId]);
      setIsCreateOpen(false);
      setCreateData({ email: '', first_name: '', last_name: '', password: '', global_role: 'viewer', event_id: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create member')
  });

  // Apply template to existing member
  const applyTemplateMutation = useMutation({
    mutationFn: ({ memberId, templateId }) => 
      api.put(`/admin/team/${memberId}/apply-template?template_id=${templateId}`),
    onSuccess: () => {
      toast.success('Role template applied successfully');
      queryClient.invalidateQueries(['team-members', scopedEventId]);
      setApplyTemplateTarget(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to apply template')
  });

  // Update member mutation
  const updateMemberMutation = useMutation({
    mutationFn: ({ id, data }) => {
      if (scopedEventId) {
        const membershipId = data?.membership_id || id;
        // Event-scoped update only accepts role/permissions/is_active
        const body = {};
        if (data.role !== undefined || data.global_role !== undefined) body.role = data.role || data.global_role;
        if (data.permissions !== undefined) body.permissions = data.permissions;
        if (data.is_active !== undefined) body.is_active = data.is_active;
        return api.put(`/events/${scopedEventId}/team/${membershipId}`, body);
      }
      return api.put(`/admin/team/${id}`, data);
    },
    onSuccess: () => {
      toast.success('Member updated successfully');
      queryClient.invalidateQueries(['team-members', scopedEventId]);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to update member')
  });

  // Delete/deactivate member mutation
  const deleteMemberMutation = useMutation({
    mutationFn: ({ id, membershipId, hard }) => {
      if (scopedEventId) {
        // For event-scoped mode, prefer membership_id, fallback to admin_id
        const deleteId = membershipId || id;
        return api.delete(`/events/${scopedEventId}/team/${deleteId}`);
      }
      return api.delete(`/admin/team/${id}${hard ? '?hard=true' : ''}`);
    },
    onSuccess: (res) => {
      const hard = res?.data?.hard_delete;
      toast.success(scopedEventId ? 'Removed from event team' : (hard ? 'Member permanently deleted' : 'Member deactivated'));
      queryClient.invalidateQueries(['team-members', scopedEventId]);
      setSelectedMember(null);
    },
    onError: (err) => toast.error(err.response?.data?.detail || (scopedEventId ? 'Failed to remove from team' : 'Failed to deactivate member'))
  });

  // Resend invitation mutation
  const resendInviteMutation = useMutation({
    mutationFn: (id) => api.post(`/admin/invitations/${id}/resend`),
    onSuccess: (res, id) => {
      toast.success('Invitation resent');
      const inv = invitations.find((i) => i.id === id);
      if (res?.data?.invite_link) {
        setFreshLink({ email: inv?.email || '', url: absoluteLink(res.data.invite_link) });
      }
      queryClient.invalidateQueries(['invitations']);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to resend invitation')
  });

  // Revoke / cancel invitation mutation
  const revokeInviteMutation = useMutation({
    mutationFn: (id) => api.delete(`/admin/invitations/${id}`),
    onSuccess: (res) => {
      toast.success(res?.data?.status === 'deleted' ? 'Invitation deleted' : 'Invitation cancelled');
      queryClient.invalidateQueries(['invitations']);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to cancel invitation')
  });

  // Invite links are only ever available once — right after they are minted.
  // The stored invitation only holds a hash, so nothing can rebuild them later.
  const copyLink = (url) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success('Invite link copied to clipboard'))
        .catch(() => toast.error('Could not copy link'));
    } else {
      // Fallback
      const el = document.createElement('textarea');
      el.value = url; document.body.appendChild(el);
      el.select(); document.execCommand('copy'); document.body.removeChild(el);
      toast.success('Invite link copied');
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.name || roleId;
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.color || '#6b7280';
  };

  const getInitials = (member) => {
    if (member.first_name || member.last_name) {
      return `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase() || '?';
    }
    // Fallback to name field
    if (member.name) {
      const parts = member.name.split(' ');
      return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase() || '?';
    }
    return '?';
  };

  const getMemberName = (member) => {
    if (member.first_name || member.last_name) {
      return `${member.first_name || ''} ${member.last_name || ''}`.trim();
    }
    return member.name || member.email || 'Unknown';
  };

  // Filter active members - treat undefined is_active as active
  const activeMembers = members.filter(m => m.is_active !== false);
  const inactiveMembers = members.filter(m => m.is_active === false);

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-indigo-600" />
            Team Management
          </h2>
          <p className="text-slate-500">Manage team members and their access</p>
        </div>
        <div className="flex gap-2">
          {/* Create Member Dialog */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="create-member-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Team Member</DialogTitle>
                <DialogDescription>
                  Add a new team member directly with login credentials
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={createData.first_name}
                      onChange={(e) => setCreateData({ ...createData, first_name: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={createData.last_name}
                      onChange={(e) => setCreateData({ ...createData, last_name: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={createData.email}
                    onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={createData.password}
                    onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                    placeholder="Set initial password"
                  />
                </div>
                <div>
                  <Label>Global Role</Label>
                  <Select 
                    value={createData.global_role} 
                    onValueChange={(v) => setCreateData({ ...createData, global_role: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: role.color }} />
                            {role.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {events.length > 0 && (
                  <div>
                    <Label>Event Access (optional)</Label>
                    <Select 
                      value={createData.event_id || '__global__'} 
                      onValueChange={(v) => setCreateData({ ...createData, event_id: v === '__global__' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All events (global)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__global__">All events (global access)</SelectItem>
                        {events.map(event => (
                          <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave as &quot;All events&quot; for global access, or select specific event
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button 
                  onClick={() => createMemberMutation.mutate(createData)}
                  disabled={!createData.email || !createData.password || createMemberMutation.isPending}
                >
                  {createMemberMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Member
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Invite Dialog */}
          <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-member-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Add to Team
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add to Team</DialogTitle>
                <DialogDescription>
                  Pick an existing person or invite a brand-new email — the
                  system detects which and either adds them straight to the
                  team or emails them a secure invite to set up their password.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* iter-438k · bug#3 — search existing across all user pools */}
                <div>
                  <Label className="text-sm">Add someone already in the system</Label>
                  <div className="mt-1">
                    <PoolSearchAutocomplete
                      onPick={(p) => {
                        const [first = '', ...rest] = (p.name || '').split(' ');
                        setInviteData((d) => ({
                          ...d,
                          email: p.email,
                          first_name: first || d.first_name,
                          last_name: rest.join(' ') || d.last_name,
                        }));
                        toast.success(`Prefilled from ${(p.sources || []).join(', ') || 'directory'}`);
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Or fill the fields below to invite a brand-new person.
                  </p>
                </div>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    placeholder="colleague@example.com"
                    data-testid="team-invite-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name (optional)</Label>
                    <Input
                      value={inviteData.first_name}
                      onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Last Name (optional)</Label>
                    <Input
                      value={inviteData.last_name}
                      onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select 
                    value={inviteData.role_id} 
                    onValueChange={(v) => setInviteData({ ...inviteData, role_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: role.color }} />
                            {role.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {events.length > 0 && (
                  <div>
                    <Label>Event Access (optional)</Label>
                    <Select 
                      value={inviteData.event_id || '__global__'} 
                      onValueChange={(v) => setInviteData({ ...inviteData, event_id: v === '__global__' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All events (global)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__global__">All events (global role)</SelectItem>
                        {events.map(event => (
                          <SelectItem key={event.id} value={event.id}>
                            {event.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Leave empty for global access, or select a specific event
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsInviteOpen(false)}>Cancel</Button>
                <Button
                  onClick={() => inviteMutation.mutate(inviteData)}
                  disabled={!inviteData.email || inviteMutation.isPending}
                  data-testid="team-invite-submit-btn"
                >
                  {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add to Team
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members" data-testid="members-tab">
            <Users className="h-4 w-4 mr-2" />
            Team Members ({activeMembers.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="invitations-tab">
            <Mail className="h-4 w-4 mr-2" />
            Invitations ({invitations.length})
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <Shield className="h-4 w-4 mr-2" />
            Role Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4">
          {loadingMembers ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : activeMembers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeMembers.map(member => (
                <Card key={member.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={member.avatar_url} />
                        <AvatarFallback className="bg-indigo-100 text-indigo-700">
                          {getInitials(member)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold truncate">
                            {getMemberName(member)}
                          </h3>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setSelectedMember(member)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setApplyTemplateTarget(member)}>
                                <Shield className="h-4 w-4 mr-2" />
                                Apply Role Template
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-amber-600"
                                onClick={() => deleteMemberMutation.mutate({ id: member.id, membershipId: member.membership_id, hard: false })}
                                data-testid={`team-deactivate-${member.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Deactivate (keep history)
                              </DropdownMenuItem>
                              {!scopedEventId && (
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    if (window.confirm(`Permanently delete ${member.name || member.email}?\n\nThis removes the team-member record entirely so the email can be re-invited cleanly. Audit log entries created BY this member are kept.`)) {
                                      deleteMemberMutation.mutate({ id: member.id, hard: true });
                                    }
                                  }}
                                  data-testid={`team-hard-delete-${member.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Permanently delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <p className="text-sm text-slate-500 truncate">{member.email}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(member.global_role || member.role) && (
                            <Badge 
                              variant={member.role === 'super_admin' ? 'default' : 'secondary'}
                              className={member.role === 'super_admin' ? 'bg-indigo-600' : ''}
                            >
                              {member.role === 'super_admin' ? 'Super Admin' : (member.global_role || member.role)}
                            </Badge>
                          )}
                          {Object.keys(member.event_roles || {}).length > 0 && (
                            <Badge variant="outline">
                              {Object.keys(member.event_roles).length} event(s)
                            </Badge>
                          )}
                          {member.assigned_events?.length > 0 && (
                            <Badge variant="outline">
                              {member.assigned_events.length} event(s)
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {member.last_login && (
                      <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last login: {new Date(member.last_login).toLocaleDateString()}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No team members yet</p>
                <p className="text-sm text-slate-400 mt-1">
                  Create a member or send an invitation to get started
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="invitations" className="mt-4">
          {freshLink && (
            <Card className="mb-4 border-amber-300 bg-amber-50" data-testid="fresh-invite-link-card">
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      Invite link for {freshLink.email}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      Copy it now — only a hash of the token is stored, so this link cannot be shown again.
                      Use Resend to mint a new one.
                    </p>
                    <pre className="text-[11px] bg-slate-900 text-emerald-300 p-2 rounded mt-2 overflow-x-auto" data-testid="fresh-invite-link">{freshLink.url}</pre>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => copyLink(freshLink.url)} data-testid="copy-fresh-invite-link">
                      <Link2 className="h-4 w-4 mr-1" /> Copy
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setFreshLink(null)} data-testid="dismiss-fresh-invite-link">
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {loadingInvitations ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : invitations.length > 0 ? (
            <div className="space-y-3">
              {/* Status filter chips */}
              <div className="flex flex-wrap gap-2 pb-2" data-testid="invitation-status-filter">
                {['all', 'pending', 'accepted', 'expired', 'cancelled'].map((s) => {
                  const count = s === 'all'
                    ? invitations.length
                    : invitations.filter(i => i.status === s).length;
                  const active = inviteFilter === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setInviteFilter(s)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`invitation-filter-${s}`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)} · {count}
                    </button>
                  );
                })}
              </div>

              {invitations
                .filter((inv) => inviteFilter === 'all' || inv.status === inviteFilter)
                .map(invite => {
                  const status = invite.status || 'pending';
                  const statusStyle = {
                    pending:   'bg-amber-100 text-amber-700 border-amber-200',
                    accepted:  'bg-emerald-100 text-emerald-700 border-emerald-200',
                    expired:   'bg-slate-100 text-slate-600 border-slate-200',
                    cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
                  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
                  const iconBg = {
                    pending:   'bg-amber-100 text-amber-600',
                    accepted:  'bg-emerald-100 text-emerald-600',
                    expired:   'bg-slate-100 text-slate-500',
                    cancelled: 'bg-rose-100 text-rose-600',
                  }[status] || 'bg-slate-100 text-slate-500';
                  return (
                    <Card key={invite.id} data-testid={`invitation-row-${invite.id}`}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
                              <Mail className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{invite.email}</p>
                              <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                                <Badge
                                  variant="outline"
                                  style={{ borderColor: getRoleColor(invite.role_id), color: getRoleColor(invite.role_id) }}
                                >
                                  {getRoleName(invite.role_id)}
                                </Badge>
                                <Badge variant="outline" className={`uppercase tracking-wide ${statusStyle}`} data-testid={`invitation-status-${invite.id}`}>
                                  {status}
                                </Badge>
                                {invite.expires_at && (
                                  <span className="flex items-center gap-1 text-xs">
                                    <Calendar className="h-3 w-3" />
                                    {status === 'pending' ? 'Expires' : 'Expired'} {new Date(invite.expires_at).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => resendInviteMutation.mutate(invite.id)}
                                disabled={resendInviteMutation.isPending}
                                data-testid={`invitation-resend-${invite.id}`}
                                title="Email a fresh invite link"
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Resend
                              </Button>
                            )}
                            {status !== 'accepted' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => revokeInviteMutation.mutate(invite.id)}
                                title={status === 'pending' ? 'Cancel invitation' : 'Delete record'}
                                data-testid={`invitation-cancel-${invite.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              {invitations.filter(i => inviteFilter === 'all' || i.status === inviteFilter).length === 0 && (
                <Card>
                  <CardContent className="py-8 text-center text-slate-400 text-sm">
                    No invitations match the “{inviteFilter}” filter.
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No pending invitations</p>
                <p className="text-sm text-slate-400 mt-1">
                  Send an invitation to add team members
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Role Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Quick Setup Role Templates</h3>
            <p className="text-sm text-slate-500">
              Pre-configured permission bundles for common team roles. Click a template to view details or apply to a team member.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {roleTemplates.map(template => (
              <Card 
                key={template.id} 
                className="cursor-pointer hover:shadow-md transition-all hover:border-indigo-300"
                onClick={() => setSelectedTemplate(template)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                      style={{ backgroundColor: `${template.color}20` }}
                    >
                      {template.icon}
                    </div>
                    {template.is_custom && (
                      <Badge variant="outline" className="text-xs">Custom</Badge>
                    )}
                  </div>
                  <CardTitle className="text-base mt-2">{template.name}</CardTitle>
                  <CardDescription className="text-xs line-clamp-2">
                    {template.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Shield className="h-3 w-3" />
                    {template.permissions?.length || 0} permissions
                  </div>
                  {template.suggested_for && (
                    <p className="text-xs text-slate-400 mt-1 italic">
                      For: {template.suggested_for}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Template Details Dialog */}
      <Dialog open={!!selectedTemplate} onOpenChange={(open) => !open && setSelectedTemplate(null)}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                style={{ backgroundColor: `${selectedTemplate?.color}20` }}
              >
                {selectedTemplate?.icon}
              </span>
              {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4 py-4">
              {selectedTemplate.suggested_for && (
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  💡 <strong>Suggested for:</strong> {selectedTemplate.suggested_for}
                </div>
              )}
              
              <div>
                <Label className="text-sm font-semibold">Permissions Included</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedTemplate.permissions?.map(perm => (
                    <Badge key={perm} variant="outline" className="text-xs">
                      {perm.replace('.', ' › ').replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-3 block">Apply to Team Member</Label>
                <Select onValueChange={(memberId) => {
                  if (memberId) {
                    applyTemplateMutation.mutate({ memberId, templateId: selectedTemplate.id });
                    setSelectedTemplate(null);
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a team member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeMembers.map(member => (
                      <SelectItem key={member.id} value={member.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs bg-indigo-100 text-indigo-700">
                              {getInitials(member)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{getMemberName(member)}</span>
                          <span className="text-xs text-slate-400">({member.email})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Template Dialog */}
      <Dialog open={!!applyTemplateTarget} onOpenChange={(open) => !open && setApplyTemplateTarget(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Apply Role Template
            </DialogTitle>
            <DialogDescription>
              {applyTemplateTarget && (
                <>Apply a pre-configured role template to <strong>{getMemberName(applyTemplateTarget)}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3 max-h-[400px] overflow-y-auto">
            {roleTemplates.map(template => (
              <div
                key={template.id}
                className="p-4 border rounded-lg cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-all"
                onClick={() => {
                  if (applyTemplateTarget) {
                    applyTemplateMutation.mutate({ 
                      memberId: applyTemplateTarget.id, 
                      templateId: template.id 
                    });
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <span 
                    className="w-8 h-8 rounded flex items-center justify-center text-lg shrink-0"
                    style={{ backgroundColor: `${template.color}20` }}
                  >
                    {template.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{template.name}</div>
                    <p className="text-xs text-slate-500 line-clamp-2">{template.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <Shield className="h-3 w-3" />
                      {template.permissions?.length || 0} permissions
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyTemplateTarget(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={(open) => !open && setSelectedMember(null)}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Edit Team Member
            </DialogTitle>
            <DialogDescription>
              Update team member details and permissions
            </DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
              {/* Member Info Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={selectedMember.avatar_url} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-lg">
                    {getInitials(selectedMember)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg">{getMemberName(selectedMember)}</h3>
                  <p className="text-sm text-slate-500">{selectedMember.email}</p>
                  {selectedMember.created_at && (
                    <p className="text-xs text-slate-400 mt-1">
                      Joined {new Date(selectedMember.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              {/* Edit Form */}
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-first-name">First Name</Label>
                    <Input
                      id="edit-first-name"
                      value={selectedMember.first_name || ''}
                      onChange={(e) => setSelectedMember({...selectedMember, first_name: e.target.value})}
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-last-name">Last Name</Label>
                    <Input
                      id="edit-last-name"
                      value={selectedMember.last_name || ''}
                      onChange={(e) => setSelectedMember({...selectedMember, last_name: e.target.value})}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={selectedMember.email || ''}
                    onChange={(e) => setSelectedMember({...selectedMember, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-role">Role</Label>
                  <Select 
                    value={selectedMember.global_role || selectedMember.role || 'viewer'}
                    onValueChange={(v) => setSelectedMember({...selectedMember, global_role: v, role: v})}
                  >
                    <SelectTrigger id="edit-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: role.color || '#6b7280' }} />
                            <span>{role.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    {roles.find(r => r.id === (selectedMember.global_role || selectedMember.role))?.description || ''}
                  </p>
                </div>

                {events.length > 0 && (
                  <div>
                    <Label>Event Access</Label>
                    <Select 
                      value={selectedMember.assigned_events?.[0] || '__all__'}
                      onValueChange={(v) => {
                        if (v === '__all__') {
                          setSelectedMember({...selectedMember, assigned_events: [], event_roles: {}});
                        } else {
                          setSelectedMember({
                            ...selectedMember, 
                            assigned_events: [v],
                            event_roles: { [v]: selectedMember.global_role || selectedMember.role || 'viewer' }
                          });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select event access" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All events (global access)</SelectItem>
                        {events.map(event => (
                          <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Select specific event or allow access to all events
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="edit-password">New Password (optional)</Label>
                  <Input
                    id="edit-password"
                    type="password"
                    value={selectedMember.newPassword || ''}
                    onChange={(e) => setSelectedMember({...selectedMember, newPassword: e.target.value})}
                    placeholder="Leave blank to keep current password"
                  />
                </div>
              </div>

              {/* Current Permissions Display */}
              {selectedMember.permissions && selectedMember.permissions.length > 0 && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500">Current Permissions</Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedMember.permissions.slice(0, 5).map(perm => (
                      <Badge key={perm} variant="outline" className="text-xs">
                        {perm === '*' ? 'All Permissions' : perm}
                      </Badge>
                    ))}
                    {selectedMember.permissions.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedMember.permissions.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-shrink-0 gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setSelectedMember(null)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                const updateData = {
                  first_name: selectedMember.first_name,
                  last_name: selectedMember.last_name,
                  email: selectedMember.email,
                  global_role: selectedMember.global_role || selectedMember.role,
                  assigned_events: selectedMember.assigned_events,
                  event_roles: selectedMember.event_roles
                };
                if (selectedMember.newPassword) {
                  updateData.password = selectedMember.newPassword;
                }
                updateMemberMutation.mutate({ id: selectedMember.id, data: updateData });
                setSelectedMember(null);
              }}
              disabled={updateMemberMutation.isPending}
            >
              {updateMemberMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
